import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FieldConfigurationList } from 'src/app/field-configuration/model/fieldConfigurationList';
import { FieldConfigurationEdit } from 'src/app/field-configuration/model/fieldConfigurationEdit';
import { DictionaryService } from './dictionary.service';
import { Observable, Subject } from 'rxjs';
import { of } from 'rxjs';
import { Dictionary } from '../model/dictionary';
import { map } from 'lodash';
import { Cacheable, CacheBuster } from 'ts-cacheable';

const fieldConfigurationsCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class FieldConfigurationService
{

  constructor(
    private http: HttpClient,
    private dictionaryService: DictionaryService) { }

  /**
   * Get full field configurations object.
   */
  @Cacheable(
    {
      cacheBusterObserver: fieldConfigurationsCacheBuster
    })
  public getCachedFieldConfigurations(activeOnly = false): Observable<FieldConfigurationList[]>
  {
    return this.http.get<FieldConfigurationList[]>('/field/configurations?activeonly=' + activeOnly);
  }

  /**
   * Delete a Field Configuration.
   *
   * @param id FieldConfiguration id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: fieldConfigurationsCacheBuster
    })
  public deleteFieldConfiguration(id: number): Observable<FieldConfigurationList>
  {
    return this.http.delete<FieldConfigurationList>(`/field/configuration/${id}`);
  }

  /**
   * Save a Field Configuration.
   *
   * @param id FieldConfiguration id
   * @param data FieldConfiguration to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: fieldConfigurationsCacheBuster
    })
  public saveFieldConfiguration(id: number, data: any, isNew: boolean): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/field/configuration', data);
    }
    else
    {
      return this.http.put('/field/configuration', data);
    }
  }

  /**
   * Get partial field configurations for grid list.
   */
  public getFieldConfigurationsList(): Observable<FieldConfigurationList[]>
  {
    return this.http.get<FieldConfigurationList[]>('/field/configurations/list');
  }

  /**
   * Get full field configurations object.
   */
  public getFieldConfigurations(activeOnly = false): Observable<FieldConfigurationList[]>
  {
    return this.http.get<FieldConfigurationList[]>(`/field/configurations?activeonly=${activeOnly}`);
  }

  /**
   * Get all field configuration.
   *
   * @param id FieldConfiguration id
   */
  public getFieldConfigurationById(id: number): Observable<FieldConfigurationEdit>
  {
    return this.http.get<FieldConfigurationEdit>(`/field/configuration/${id}`);
  }

  /**
   * Check Field Configuration against XML
   *
   * @param data FieldConfiguration query and XML
   */
  public checkField(data: any): Observable<any>
  {
    return this.http.post('/field/check', data);
  }

  /**
   * Check if Field Configuration is in use
   *
   * @param data FieldConfigurationId
   */
  public existsFieldConfiguration(id: number): Observable<any>
  {
    return this.http.get(`/field/configuration/inuse/${id}`);
  }

  /**
   * Return an observable that contains the codes for all code sets for which Field Configurations exists.
   *
   * @param listFields List of fields configurations
   * @returns observable
   */
  public getCodeSetCodesUsedByFieldConfigurations(listFields: FieldConfigurationList[]): Observable<any[]>
  {
    // Check first if there are Field Configurations with a Code Set assigned.
    const filtered = listFields.filter(r => r.codeSet);

    // If none, returns an empty Observable to make sure subscribe is always executed.
    if (filtered.length === 0)
    {
      return of([]);
    }
    else
    {
      return this.dictionaryService.getCachedCodesUsedByFieldConfigurations();
    }
  }

  /**
   * Set the Query Field options based on CodeSets Codes.
   *
   * @param listFields List of fields configurations
   * @param codeSetCodes CodeSet Codes for this Field Configuration
   */
  public setCodeSetCodesUsedByFieldConfigurations(listFields: FieldConfigurationList[], codeSetCodes: Dictionary[]): void
  {
    const filtered = listFields.filter(r => r.codeSet !== null);
    if (filtered.length >= 0)
    {
      // Processing all filtered Field Configurations with Code Set assigned
      filtered.forEach(d =>
      {
        // Get the codes to use
        const filteredCodes = codeSetCodes.filter(x => x.codeSet !== null && x.codeSet.id === d.codeSet.id);
        if (filteredCodes.length > 0)
        {
          // Transform Codes to Field Configuration options array
          const fieldConfigurationOptions = map(filteredCodes, (item: Dictionary) =>
          {
            // When a codeSystem is used, we must save the CodeSystem^Code
            const codeSystem = item.codeSystem ? `${item.codeSystem}^` : '';
            return { name: `${item.displayName} (${item.code})`, value: `${codeSystem}${item.code}` };
          });
          d.options = fieldConfigurationOptions;
        }
      });
    }
  }

  /**
   * Check if Field Configuration Name exists to avoid name duplicates
   *
   * @param name Field Configuration Name
   * @param id Field Condiguration Id
   */
  public existsFieldConfigurationName(name: string): Observable<any>
  {
    return this.http.get(`/field/configuration/exists?name=${name}`);
  }

  public fieldConfigurationInUse(field: string, value: string): Observable<any>
  {
    return this.http.get(`/field/query/inuse/${field}/${value}`);
  }
}
